import { SignupState } from '@/types/Signup';
import { SetupIntentStatus } from './checkoutSession';
import { ApplicationFormValues } from './applicationValidation';

/**
 * Applies "my billing address is the same as my shipping address".
 *
 * The application collects two addresses, the same as the multi-page flow: a
 * shipping address in its own section, and a billing address alongside the card.
 * When the member says they match, the shipping address is copied into the
 * billing fields at submit — the same thing `SignupStep4Form` does, with the
 * same field names, so both flows hand `createAddressPayloadFromAddressInfo`
 * the shape it already expects.
 *
 * Copying rather than collapsing is what keeps the two addresses genuinely
 * separate when they differ: `SignupState` stores `address/city/state/postalCode`
 * as shipping and `billing*` as billing, and the registration endpoint writes a
 * record for each. Forcing them equal would drop capability the live signup has
 * — and would send the shipping ZIP to Stripe for AVS, which fails for anyone
 * whose card bills somewhere else.
 */
export function applyBillingMirror(
  values: ApplicationFormValues
): ApplicationFormValues & Partial<SignupState> {
  if (!values.sameAsShippingAddress) {
    return values;
  }

  return {
    ...values,
    billingAddress: values.address,
    billingAptUnitSuite: values.aptUnitSuite,
    billingCity: values.city,
    billingState: values.state,
    billingPostalCode: values.postalCode
  };
}

/** The billing fields a wallet's address replaces; `{}` for a card or no zip. */
export function walletBillingOverride(
  paymentMethod: SetupIntentStatus['paymentMethod'],
  committed: Partial<SignupState>
): Partial<SignupState> {
  const address = paymentMethod?.wallet
    ? paymentMethod.billingAddress
    : undefined;

  if (!address?.postal_code) {
    return {};
  }

  return {
    // The address payload only reads the billing fields when this is false.
    sameAsShippingAddress: false,
    billingAddress: address.line1 ?? committed.billingAddress,
    billingAptUnitSuite: address.line2 ?? '',
    billingCity: address.city ?? committed.billingCity,
    billingState: address.state ?? committed.billingState,
    billingPostalCode: address.postal_code
  };
}

/**
 * The form values that put the billing address Stripe just checked in front of
 * the member: "same as shipping" unticked, billing fields filled from what was
 * submitted (the wallet's address, or the mirrored shipping address).
 */
export function billingFromSubmission(
  submitted: Partial<SignupState>
): Partial<ApplicationFormValues> {
  return {
    sameAsShippingAddress: false,
    billingAddress: submitted.billingAddress ?? '',
    billingAptUnitSuite: submitted.billingAptUnitSuite ?? '',
    billingCity: submitted.billingCity ?? '',
    billingState: submitted.billingState ?? '',
    billingPostalCode: submitted.billingPostalCode ?? ''
  };
}

/**
 * What the member sees above Billing's Continue after a postal-code refusal.
 * `paymentMethodName` is the wallet that supplied the address ("Google Pay");
 * without one, the member typed it themselves.
 */
export function postalCodeMismatchNotice(paymentMethodName?: string): string {
  const source = paymentMethodName
    ? `supplied by ${paymentMethodName}`
    : 'you entered';

  return `The billing address ${source} doesn't match your card. Please enter the correct billing address for your card and try again.`;
}

export function walletBillingMissing(
  paymentMethod: SetupIntentStatus['paymentMethod']
): boolean {
  return !!paymentMethod?.wallet && !paymentMethod.billingAddress?.postal_code;
}
